import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../i18n';
import { useUser } from '../../context/UserContext';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { visibleCategories as categories } from '@barter/shared';
import { DESIGNS } from '../../services/design';
import { Icon } from '../../components/Icon/Icon';
import { sendFeedback, APP_VERSION, type FeedbackCategory } from '../../services/feedback';
import { useInstall } from '../../hooks/useInstall';
import { promptInstall } from '../../services/install';
import {
  currentPushState,
  enablePush,
  disablePush,
  sendTestPush,
  type PushState,
} from '../../services/push';

// Настройки: базовые (профиль, оформление, язык) — сразу;
// остальное (подписки, связь с разработчиком, о приложении) — в свёрнутом
// блоке «Додатково». Ссылки вида /settings#feedback раскрывают блок сами.

const FEEDBACK_CATS: FeedbackCategory[] = ['bug', 'idea', 'question'];

// Установка как приложения (barter-025, по образцу Айлы install.js):
// Android/desktop — нативный диалог по кнопке (beforeinstallprompt перехватывает
// initInstall в main.tsx), iOS Safari — единственный путь «Поделиться → На
// экран „Домой“», показываем шаги. В Telegram Mini App установки нет — карточка
// скрыта (проверка isTelegram на месте вызова).
function InstallCard() {
  const { t } = useTranslation();
  const { installed, canPrompt, platform } = useInstall();
  const [iosOpen, setIosOpen] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (installed) {
    return (
      <div className="settings__card">
        <h3 className="settings__subtitle">{t('settings.app')}</h3>
        <div className="settings__row">✅ {t('pwa.installed')}</div>
      </div>
    );
  }

  async function handleInstall() {
    setInstalling(true);
    const res = await promptInstall();
    setInstalling(false);
    if (res.outcome === 'dismissed') setDismissed(true);
  }

  return (
    <div className="settings__card">
      <h3 className="settings__subtitle">{t('settings.app')}</h3>
      {canPrompt ? (
        <>
          <p className="settings__hint">{t('pwa.installHint')}</p>
          {dismissed && <p className="settings__hint">{t('pwa.menuHint')}</p>}
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void handleInstall()}
            disabled={installing}
          >
            {installing ? t('common.loading') : `⬇️ ${t('pwa.install')}`}
          </button>
        </>
      ) : platform === 'ios' ? (
        <>
          <button type="button" className="btn" onClick={() => setIosOpen((v) => !v)}>
            {t('pwa.iosInstall')}
          </button>
          {iosOpen && (
            <ol className="install-steps">
              <li>{t('pwa.iosStep1')}</li>
              <li>{t('pwa.iosStep2')}</li>
              <li>{t('pwa.iosStep3')}</li>
            </ol>
          )}
        </>
      ) : (
        // desktop/Android до события beforeinstallprompt — браузеры без него
        <p className="settings__hint">{t('pwa.menuHint')}</p>
      )}
    </div>
  );
}

// Уведомления о новых объявлениях (barter-026): Web Push из PWA по образцу
// Айлы. Тумблер = разрешение браузера + подписка на сервере; «Перевірити»
// шлёт тестовое уведомление. Работает в обоих режимах (веб и ТГ), где
// браузер поддерживает PushManager (в webview Telegram может отсутствовать).
function NotificationsCard() {
  const { t } = useTranslation();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    void currentPushState().then(setState);
  }, []);

  if (!state) return null;
  if (!state.supported) {
    return (
      <div className="settings__card">
        <h3 className="settings__subtitle">🔔 {t('push.title')}</h3>
        <p className="settings__hint">{t('push.unsupported')}</p>
      </div>
    );
  }

  const on = state.permission === 'granted' && state.subscribed;

  async function handleToggle() {
    setBusy(true);
    setNote(null);
    try {
      if (on) {
        await disablePush();
      } else {
        const res = await enablePush();
        if (!res.ok && res.reason === 'permission') setNote(t('push.denied'));
      }
      setState(await currentPushState());
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setBusy(true);
    setNote(null);
    try {
      const res = await sendTestPush();
      setNote(t(res.sent > 0 ? 'push.testSent' : 'push.testFail', { n: res.sent }));
    } catch {
      setNote(t('push.testFail', { n: 0 }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__card">
      <h3 className="settings__subtitle">🔔 {t('push.title')}</h3>
      <p className="settings__hint">{t('push.hint')}</p>
      {state.permission === 'denied' && !on && (
        <p className="settings__hint">{t('push.denied')}</p>
      )}
      <div className="subs-item__row">
        <span className="subs-item__label">
          <span className="subs-item__icon">{on ? '✅' : '🔕'}</span>
          <span className="subs-item__name">{on ? t('push.on') : t('push.off')}</span>
        </span>
        <label className="subs-toggle">
          <input
            type="checkbox"
            checked={on}
            disabled={busy}
            onChange={() => void handleToggle()}
          />
          <span className="subs-toggle__slider" />
        </label>
      </div>
      {on && (
        <>
          <p className="settings__hint">{t('push.criteriaHint')}</p>
          <button
            type="button"
            className="btn btn--small"
            style={{ marginTop: 8 }}
            disabled={busy}
            onClick={() => void handleTest()}
          >
            {t('push.test')}
          </button>
        </>
      )}
      {note && <p className="settings__hint">{note}</p>}
    </div>
  );
}

export function SettingsPage() {
  const location = useLocation();
  const {
    user,
    userReady,
    renameUser,
    isTelegram,
    design,
    setDesign,
    categoryStyle,
    setCategoryStyle,
    themePreference,
    setThemePreference,
    uiMode,
    setUiMode,
  } = useUser();
  const { t, i18n } = useTranslation();
  const { subscriptions, loading: subsLoading, toggle, create } = useSubscriptions();
  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [fbCategory, setFbCategory] = useState<FeedbackCategory>('bug');
  const [fbMessage, setFbMessage] = useState('');
  const [fbContact, setFbContact] = useState('');
  const [fbState, setFbState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Веб-профиль (barter-024): имя редактируется, хранится в этом браузере
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaved, setNameSaved] = useState(false);

  useEffect(() => {
    if (user?.source === 'web') setNameDraft(user.firstName);
  }, [user?.source, user?.firstName]);

  async function handleNameSave() {
    if (!nameDraft.trim() || nameDraft.trim() === user?.firstName) return;
    await renameUser(nameDraft);
    setNameSaved(true);
    window.setTimeout(() => setNameSaved(false), 2500);
  }

  // Deep-link /settings#feedback (кнопка «Сообщить разработчику» из ошибок):
  // раскрываем «Додатково» и прокручиваем к форме
  useEffect(() => {
    if (location.hash === '#feedback') {
      setAdvancedOpen(true);
      const timer = setTimeout(() => {
        document.getElementById('feedback')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const languages = [
    { code: 'uk', label: 'Українська' },
    { code: 'ru', label: 'Русский' },
    { code: 'ro', label: 'Română' },
    { code: 'en', label: 'English' },
  ];

  const themeOptions = [
    { value: 'dark', label: t('settings.themeDark') },
    { value: 'light', label: t('settings.themeLight') },
    { value: 'system', label: t('settings.themeSystem') },
  ] as const;

  const activeCount = subscriptions.filter((s) => s.active).length;

  function isSubscribed(categoryId: string): string | undefined {
    return subscriptions.find((s) => s.categoryId === categoryId && s.active)?.id;
  }

  function handleToggle(categoryId: string) {
    const subId = isSubscribed(categoryId);
    if (subId) {
      toggle(subId, false);
    } else {
      const existing = subscriptions.find((s) => s.categoryId === categoryId);
      if (existing) {
        toggle(existing.id, true);
      } else {
        create(categoryId, undefined).catch(() => {});
      }
    }
  }

  async function handleFeedbackSend() {
    const message = fbMessage.trim();
    if (message.length < 5 || fbState === 'sending') return;
    setFbState('sending');
    try {
      await sendFeedback({
        category: fbCategory,
        message,
        contact: fbContact.trim() || undefined,
      });
      setFbState('sent');
    } catch {
      setFbState('error');
    }
  }

  return (
    <div className="settings">
      <h2 className="section-title">{t('settings.title')}</h2>

      <div className="settings__card">
        <h3 className="settings__subtitle">{t('settings.profile')}</h3>
        {!userReady ? (
          <div className="settings__loading">{t('common.loading')}</div>
        ) : user?.source === 'web' ? (
          <div className="settings__profile">
            <div className="settings__row">
              <span>{t('settings.webNameLabel')}</span>
              <input
                className="form-input form-input--small"
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder={t('settings.webNamePlaceholder')}
                maxLength={40}
                style={{ width: 'auto', minWidth: 140 }}
              />
            </div>
            <button
              type="button"
              className="btn btn--small"
              onClick={() => void handleNameSave()}
              disabled={!nameDraft.trim() || nameDraft.trim() === user.firstName}
              style={{ marginTop: 8 }}
            >
              {nameSaved ? `✅ ${t('settings.webProfileSaved')}` : t('settings.webProfileSave')}
            </button>
            <div className="settings__id">ID: {user.id}</div>
            <p className="settings__hint">{t('settings.webProfileHint')}</p>
          </div>
        ) : user ? (
          <div className="settings__profile">
            <div className="settings__name">
              {user.firstName}
              {user.lastName ? ` ${user.lastName}` : ''}
            </div>
            {user.username && <div className="settings__username">@{user.username}</div>}
            <div className="settings__id">ID: {user.id}</div>
          </div>
        ) : (
          <div className="settings__no-user">{t('myAds.noProfile')}</div>
        )}
      </div>

      <div className="settings__card">
        <h3 className="settings__subtitle">{t('settings.modeTitle')}</h3>
        <p className="settings__hint">{t('settings.modeHint')}</p>
        <div className="seg-switch">
          <button
            type="button"
            className={`seg-switch__btn${uiMode === 'simple' ? ' seg-switch__btn--active' : ''}`}
            onClick={() => setUiMode('simple')}
          >
            {t('settings.modeSimple')}
          </button>
          <button
            type="button"
            className={`seg-switch__btn${uiMode === 'full' ? ' seg-switch__btn--active' : ''}`}
            onClick={() => setUiMode('full')}
          >
            {t('settings.modeFull')}
          </button>
        </div>
      </div>

      <div className="settings__card">
        <h3 className="settings__subtitle">{t('settings.design')}</h3>
        <p className="settings__hint">{t('design.hint')}</p>
        <div className="design-picker">
          {DESIGNS.map((d) => (
            <button
              key={d.id}
              className={`design-picker__item${design === d.id ? ' design-picker__item--active' : ''}`}
              onClick={() => setDesign(d.id)}
            >
              <span className="design-picker__swatch" style={{ background: d.swatch }} />
              <span className="design-picker__name">{t(d.nameKey)}</span>
              <span className="design-picker__desc">{t(`${d.nameKey}Desc`)}</span>
            </button>
          ))}
        </div>

        <div className="settings__row" style={{ marginTop: 12 }}>
          <span>{t('settings.theme')}</span>
          <div className="seg-switch">
            {themeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`seg-switch__btn${themePreference === opt.value ? ' seg-switch__btn--active' : ''}`}
                onClick={() => setThemePreference(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="settings__row" style={{ marginTop: 12 }}>
          <span>{t('design.iconsLabel')}</span>
          <div className="cat-style-switch">
            <button
              className={`cat-style-switch__btn${categoryStyle === 'photo' ? ' cat-style-switch__btn--active' : ''}`}
              onClick={() => setCategoryStyle('photo')}
            >
              {t('design.iconsPhoto')}
            </button>
            <button
              className={`cat-style-switch__btn${categoryStyle === 'emoji' ? ' cat-style-switch__btn--active' : ''}`}
              onClick={() => setCategoryStyle('emoji')}
            >
              {t('design.iconsEmoji')}
            </button>
          </div>
        </div>
      </div>

      <div className="settings__card">
        <h3 className="settings__subtitle">{t('settings.language')}</h3>
        <div className="settings__row">
          <span>{t('settings.language')}</span>
          <select
            className="form-select form-select--small"
            value={i18n.language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{ width: 'auto', minWidth: 120 }}
          >
            {languages.map((lng) => (
              <option key={lng.code} value={lng.code}>
                {lng.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Установка PWA — только в обычном браузере (barter-025) */}
      {!isTelegram && <InstallCard />}

      {/* Уведомления о новых объявлениях (barter-026) — в обоих режимах:
          в обычном браузере подписка устройства, в webview Telegram карточка
          показывает честное состояние поддержки */}
      <NotificationsCard />

      <button
        type="button"
        className={`settings-advanced__toggle${advancedOpen ? ' settings-advanced__toggle--open' : ''}`}
        onClick={() => setAdvancedOpen((v) => !v)}
        aria-expanded={advancedOpen}
      >
        {t('settings.advanced')}
        <span className="settings-advanced__chevron">
          <Icon name="chevron-down" size={18} />
        </span>
      </button>

      {advancedOpen && (
        <>
          <div className="settings__card">
            <h3 className="settings__subtitle">
              {t('subscriptions.title')}
              <span className="settings__counter">{activeCount}/10</span>
            </h3>
            <p className="settings__hint">{t('subscriptions.description')}</p>

            {subsLoading ? (
              <div className="settings__loading">{t('common.loading')}</div>
            ) : (
              <div className="subs-list">
                {categories.map((cat) => {
                  const hasSubs = cat.subcategories && cat.subcategories.length > 0;
                  const subId = isSubscribed(cat.id);
                  const isExpanded = expandedCat === cat.id;
                  const childSubCount = cat.subcategories
                    ? cat.subcategories.filter((sc) => isSubscribed(sc.id)).length
                    : 0;

                  return (
                    <div key={cat.id} className="subs-item">
                      <div className="subs-item__row">
                        <button
                          className="subs-item__label"
                          onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                        >
                          <span className="subs-item__icon">{cat.icon}</span>
                          <span className="subs-item__name">
                            {cat.nameKey ? t(cat.nameKey) : cat.name}
                          </span>
                          {hasSubs && childSubCount > 0 && (
                            <span className="subs-item__badge">{childSubCount}</span>
                          )}
                        </button>

                        <label className="subs-toggle">
                          <input
                            type="checkbox"
                            checked={!!subId}
                            onChange={() => handleToggle(cat.id)}
                          />
                          <span className="subs-toggle__slider" />
                        </label>
                      </div>

                      {isExpanded && hasSubs && (
                        <div className="subs-item__children">
                          {cat.subcategories!.map((sc) => {
                            const scSubId = isSubscribed(sc.id);
                            return (
                              <div key={sc.id} className="subs-item subs-item--child">
                                <div className="subs-item__row">
                                  <span className="subs-item__label">
                                    <span className="subs-item__icon">{sc.icon}</span>
                                    <span className="subs-item__name">
                                      {sc.nameKey ? t(sc.nameKey) : sc.name}
                                    </span>
                                  </span>
                                  <label className="subs-toggle">
                                    <input
                                      type="checkbox"
                                      checked={!!scSubId}
                                      onChange={() => handleToggle(sc.id)}
                                    />
                                    <span className="subs-toggle__slider" />
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="settings__card" id="feedback">
            <h3 className="settings__subtitle">✉️ {t('settings.feedbackTitle')}</h3>
            <p className="settings__hint">{t('settings.feedbackHint')}</p>

            {fbState === 'sent' ? (
              <div className="feedback-sent">
                <div className="feedback-sent__text">✅ {t('settings.feedbackSent')}</div>
                <button
                  type="button"
                  className="feedback-sent__again"
                  onClick={() => {
                    setFbMessage('');
                    setFbState('idle');
                  }}
                >
                  {t('settings.feedbackAgain')}
                </button>
              </div>
            ) : (
              <>
                <div className="feedback-cats">
                  {FEEDBACK_CATS.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`feedback-cat${fbCategory === cat ? ' feedback-cat--active' : ''}`}
                      onClick={() => setFbCategory(cat)}
                    >
                      {t(`settings.feedbackCat.${cat}`)}
                    </button>
                  ))}
                </div>
                <textarea
                  className="form-input feedback-textarea"
                  value={fbMessage}
                  onChange={(e) => setFbMessage(e.target.value)}
                  placeholder={t('settings.feedbackMessagePlaceholder')}
                  maxLength={2000}
                  rows={4}
                />
                <label className="feedback-contact">
                  <span className="feedback-contact__label">{t('settings.feedbackContactLabel')}</span>
                  <input
                    className="form-input form-input--small"
                    type="text"
                    value={fbContact}
                    onChange={(e) => setFbContact(e.target.value)}
                    placeholder={user?.username ? `@${user.username}` : t('settings.feedbackContactPlaceholder')}
                    maxLength={120}
                  />
                </label>
                {fbState === 'error' && <div className="feedback-error">{t('settings.feedbackError')}</div>}
                <button
                  type="button"
                  className="btn btn--primary feedback-send"
                  disabled={fbMessage.trim().length < 5 || fbState === 'sending'}
                  onClick={handleFeedbackSend}
                >
                  {fbState === 'sending' ? t('settings.feedbackSending') : t('settings.feedbackSend')}
                </button>
              </>
            )}
          </div>

          <div className="settings__card">
            <h3 className="settings__subtitle">{t('settings.about')}</h3>
            <div className="settings__row">
              <span>{t('settings.platform')}</span>
              <span>{isTelegram ? 'Telegram' : t('settings.browser')}</span>
            </div>
            <div className="settings__row">
              <span>{t('settings.version')}</span>
              <span>{APP_VERSION}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
